/**
 * Filtrování katalogu komponent (GET …/components/tree) podle množiny SAP kódů.
 * Používá stejný tvar uzlů jako web/Android (label, children, items s polem sap).
 */

/** Minimální tvar listu v katalogu */
export interface MaterialCatalogLeaf {
  sap: string;
  [key: string]: unknown;
}

/** Minimální tvar uzlu stromu z API components/tree */
export interface MaterialCatalogTreeNode<TLeaf extends MaterialCatalogLeaf = MaterialCatalogLeaf> {
  label: string;
  type?: string;
  value?: string;
  children?: MaterialCatalogTreeNode<TLeaf>[];
  items?: TLeaf[];
}

function normalizeSap(s: string): string {
  return String(s ?? '').trim();
}

/**
 * Projde strom a vrátí mapu SAP → první nalezený list (pro doplnění názvu z API).
 */
export function indexMaterialLeavesBySap<TLeaf extends MaterialCatalogLeaf>(
  nodes: MaterialCatalogTreeNode<TLeaf>[] | undefined,
  into: Map<string, TLeaf> = new Map()
): Map<string, TLeaf> {
  if (!nodes) return into;
  for (const node of nodes) {
    for (const it of node.items ?? []) {
      const k = normalizeSap(it.sap);
      if (k && !into.has(k)) into.set(k, it);
    }
    if (node.children?.length) indexMaterialLeavesBySap(node.children, into);
  }
  return into;
}

/**
 * SAPs attached directly to this folder (depth 1). Nested subfolders are ignored.
 * Clicking a category that only has children folders yields an empty set.
 */
export function collectDirectSapsFromNode<TLeaf extends MaterialCatalogLeaf>(
  node: Pick<MaterialCatalogTreeNode<TLeaf>, 'items'>,
): Set<string> {
  const out = new Set<string>();
  for (const item of node.items ?? []) {
    const sap = normalizeSap(item.sap);
    if (sap) out.add(sap);
  }
  return out;
}

/**
 * Products assigned directly to this category id (not nested subfolders).
 * Same rule as `collectDirectSapsFromNode` for flat catalog lists.
 */
export function filterItemsByDirectCategory<T extends { categoryId?: string | null }>(
  items: readonly T[],
  categoryId: string,
): T[] {
  const id = String(categoryId ?? '').trim();
  if (!id) return [...items];
  return items.filter((i) => String(i.categoryId ?? '').trim() === id);
}

/**
 * Ponechá jen listy se SAP z `allowed` a odstraní prázdné složky.
 * Použití: převody — strom jen ze zásob zdrojového skladu (OD).
 */
export function pruneMaterialTreeByAllowedSap<TLeaf extends MaterialCatalogLeaf>(
  nodes: MaterialCatalogTreeNode<TLeaf>[] | undefined,
  allowed: ReadonlySet<string> | Iterable<string>,
): MaterialCatalogTreeNode<TLeaf>[] {
  const allowedSet =
    allowed instanceof Set
      ? (allowed as Set<string>)
      : new Set(
          [...allowed].map((s) => normalizeSap(String(s))).filter(Boolean),
        );
  return pruneNodes(nodes, allowedSet);
}

function pruneNodes<TLeaf extends MaterialCatalogLeaf>(
  nodes: MaterialCatalogTreeNode<TLeaf>[] | undefined,
  allowed: Set<string>,
): MaterialCatalogTreeNode<TLeaf>[] {
  const out: MaterialCatalogTreeNode<TLeaf>[] = [];
  for (const node of nodes ?? []) {
    const children = pruneNodes(node.children, allowed);
    const items = (node.items ?? []).filter((item) => {
      const sap = normalizeSap(item.sap);
      return !!sap && allowed.has(sap);
    });
    if (children.length === 0 && items.length === 0) continue;
    out.push({
      ...node,
      children,
      items,
    });
  }
  return out;
}
